"use client";

import {
  registerWithEmail,
  registerWithPhone,
  resendVerificationByEmail,
  resendVerificationByPhone,
} from "@/actions/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { toast } from "sonner";

import { useParams } from "next/navigation";
import lang from "./lang.json";
import { SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import { useRouter } from "next/navigation";
import { localizedPath } from "@/lib/localePath";

type Props = {
  tab: "email" | "phone";
};

const createEmailSchema = (locale: SupportedLanguage) =>
  z
    .object({
      fullName: z
        .string()
        .min(2, lang["validation-name-min"][locale])
        .max(50, lang["validation-name-max"][locale]),
      email: z.string().email(lang["validation-email-invalid"][locale]).min(2).max(50),
      password: z
        .string()
        .min(6, lang["validation-password-min"][locale])
        .max(50),
      confirmPassword: z
        .string()
        .min(6, lang["validation-password-min"][locale])
        .max(50),
      role: z.enum(["Personal", "Company"], { message: lang["validation-role-required"][locale] }),
      countryShortName: z.string({ message: lang["validation-country-required"][locale] }),
      accept: z.boolean().refine((val) => val === true, {
        message: lang["validation-accept-terms"][locale],
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: lang["validation-passwords-not-match"][locale],
      path: ["confirmPassword"],
    });

const createPhoneSchema = (locale: SupportedLanguage) =>
  z
    .object({
      fullName: z
        .string()
        .min(2, lang["validation-name-min"][locale])
        .max(50, lang["validation-name-max"][locale]),
      phone: z
        .string()
        .min(10, lang["validation-phone-min"][locale])
        .max(15, lang["validation-phone-max"][locale])
        .regex(/^\+?[0-9]+$/, lang["validation-phone-format"][locale]),
      password: z
        .string()
        .min(6, lang["validation-password-min"][locale])
        .max(50),
      confirmPassword: z
        .string()
        .min(6, lang["validation-password-min"][locale])
        .max(50),
      role: z.enum(["Personal", "Company"], { message: lang["validation-role-required"][locale] }),
      accept: z.boolean().refine((val) => val === true, {
        message: lang["validation-accept-terms"][locale],
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: lang["validation-passwords-not-match"][locale],
      path: ["confirmPassword"],
    });

export type EmailFormValues = z.infer<ReturnType<typeof createEmailSchema>>;
export type PhoneFormValues = z.infer<ReturnType<typeof createPhoneSchema>>;

function useRegister({ tab }: Props) {
  const router = useRouter();
  const params = useParams();
  const locale = resolveLocale(params.locale as string | undefined);
  const [isLoading, isLoadingSet] = useState(false);

  const emailSchema = createEmailSchema(locale);
  const phoneSchema = createPhoneSchema(locale);

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      accept: false,
    },
  });

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      fullName: "",
      phone: "",
      password: "",
      confirmPassword: "",
      accept: false,
    },
  });

  const form = tab === "email" ? emailForm : phoneForm;

  const handleEmailSubmit = async (values: EmailFormValues) => {
    isLoadingSet(true);

    const response = await registerWithEmail(
      {
        fullName: values.fullName,
        email: values.email,
        password: values.password,
        role: values.role,
        languageCode: locale || "en",
        countryShortName: values.countryShortName,
      },
      true,
    );

    isLoadingSet(false);

    if (response.status === "success" && response.metaData?.id) {
      router.push(
        localizedPath(
          locale,
          `/auth/otp?contact=${encodeURIComponent(values.email)}&user_id=${response.metaData.id}&type=email&from=register&role=${values.role}`,
        ),
      );
      return;
    }

    if (response.status === "error") {
      if (response.errorType === "account-not-verified") {
        const resResend = await resendVerificationByEmail({
          email: values.email,
          role: values.role,
        });

        if (resResend.status === "success" && resResend.metaData?.userId) {
          toast.error(lang["messages.email_not_verified"][locale]);
          router.push(
            localizedPath(
              locale,
              `/auth/otp?contact=${encodeURIComponent(values.email)}&user_id=${resResend.metaData.userId}&type=email&from=register&role=${values.role}`,
            ),
          );
          return;
        }
      }

      toast.error(response.message);
    }
  };

  const handlePhoneSubmit = async (values: PhoneFormValues) => {
    isLoadingSet(true);
    const response = await registerWithPhone({
      fullName: values.fullName,
      phone: values.phone,
      password: values.password,
      role: values.role,
      languageCode: locale || "en",
      countryShortName: "US",
    });

    isLoadingSet(false);

    if (response.status === "success" && response.metaData?.id) {
      router.push(
        localizedPath(
          locale,
          `/auth/otp?contact=${encodeURIComponent(values.phone)}&user_id=${response.metaData.id}&type=phone&from=register&role=${values.role}`,
        ),
      );
      return;
    }

    if (response.status === "error") {
      if (response.errorType === "account-not-verified") {
        const resResend = await resendVerificationByPhone({
          phone: values.phone,
          role: values.role,
        });

        if (resResend.status === "success" && resResend.metaData?.userId) {
          toast.error(lang["messages.phone_not_verified"][locale]);
          router.push(
            localizedPath(
              locale,
              `/auth/otp?contact=${encodeURIComponent(values.phone)}&user_id=${resResend.metaData.userId}&type=phone&from=register&role=${values.role}`,
            ),
          );
          return;
        }
      }

      toast.error(response.message);
    }
  };

  const onSubmit =
    tab === "email"
      ? (handleEmailSubmit as (values: EmailFormValues) => Promise<void>)
      : (handlePhoneSubmit as (values: PhoneFormValues) => Promise<void>);

  return {
    form,
    onSubmit,
    isLoading,
  };
}

export default useRegister;
